import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CaretDown, DotsThree } from 'phosphor-react';
import './MatchGroup.css';

/**
 * MatchGroup - Collapsible group of matches by date
 * 
 * @param {string} label - Group label ("Today", "Yesterday", "Oct 9")
 * @param {Array} matches - Array of match objects in this group
 * @param {Function} renderMatch - Function to render each match
 * @param {boolean} defaultExpanded - Whether group starts expanded
 */
const MatchGroup = ({ 
  label, 
  matches, 
  renderMatch,
  defaultExpanded = true,
  dateKey, // For saving custom labels
  onLabelChange, // Callback when label is edited
  onDeleteGroup, // Callback when group is deleted
  onCollapseStateChange, // Callback when collapse state changes
  isPro = true // Whether user has Pro subscription
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [isEditing, setIsEditing] = useState(false);
  const [editedLabel, setEditedLabel] = useState(label);
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  
  const toggleExpanded = () => {
    const newExpandedState = !isExpanded;
    setIsExpanded(newExpandedState);
    
    // Save collapse state to Firebase
    if (onCollapseStateChange) {
      onCollapseStateChange(dateKey, newExpandedState);
    }
  };
  
  const handleLabelDoubleClick = (e) => {
    e.stopPropagation();
    setIsEditing(true);
  };
  
  const handleLabelChange = (e) => {
    setEditedLabel(e.target.value);
  };
  
  const handleLabelBlur = () => {
    setIsEditing(false);
    if (editedLabel.trim() && editedLabel !== label && onLabelChange) {
      onLabelChange(dateKey, editedLabel.trim());
    } else {
      setEditedLabel(label); // Revert if empty or unchanged
    }
  };
  
  const handleLabelKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    } else if (e.key === 'Escape') {
      setEditedLabel(label);
      setIsEditing(false);
    }
  };
  
  const handleMenuClick = (e) => {
    e.stopPropagation();
    setShowMenu(!showMenu);
  };
  
  const handleRenameClick = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    setIsEditing(true);
  };
  
  const handleDeleteClick = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    
    const matchCount = matches.length;
    const confirmMessage = `Delete all ${matchCount} match${matchCount === 1 ? '' : 'es'} in "${editedLabel}"? This cannot be undone.`;
    
    if (window.confirm(confirmMessage)) {
      if (onDeleteGroup) {
        onDeleteGroup(dateKey, matches);
      }
    }
  };
  
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (buttonRef.current && !buttonRef.current.contains(event.target)) {
        // Check if click is outside the portal dropdown
        const portalDropdown = document.querySelector('.match-group-menu-dropdown');
        if (!portalDropdown || !portalDropdown.contains(event.target)) {
          setShowMenu(false);
        }
      }
    };
    
    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showMenu]);
  
  return (
    <div className={`match-group ${!isExpanded ? 'collapsed' : ''}`}>
      <button 
        className="match-group-header"
        onClick={toggleExpanded}
        aria-expanded={isExpanded}
        aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${label} matches`}
      >
        <CaretDown 
          size={14} 
          weight="bold" 
          className={`match-group-icon ${isExpanded ? 'expanded' : ''}`}
        />
        {isEditing ? (
          <input
            type="text"
            className="match-group-label-input"
            value={editedLabel}
            onChange={handleLabelChange}
            onBlur={handleLabelBlur}
            onKeyDown={handleLabelKeyDown}
            onClick={(e) => e.stopPropagation()}
            autoFocus
          />
        ) : (
          <span 
            className="match-group-label"
            onDoubleClick={handleLabelDoubleClick}
          >
            {editedLabel}
          </span>
        )}
        <span className="match-group-count">{matches.length}</span>
        
        {/* Menu Button - Only show for Pro users */}
        {isPro && (
          <div className="match-group-menu-wrapper" ref={menuRef}>
            <button
              ref={buttonRef}
              className="match-group-menu-button"
              onClick={handleMenuClick}
              aria-label="Group options"
              aria-expanded={showMenu}
            >
              <DotsThree size={20} weight="bold" />
            </button>
          </div>
        )}
      </button>
      
      <div className={`match-group-content ${isExpanded ? 'expanded' : 'collapsed'}`}>
        <div className="match-group-items">
          {matches.map(renderMatch)}
        </div>
      </div>

      {/* Portal dropdown menu to body for proper z-index layering */}
      {showMenu && buttonRef.current && createPortal(
        <div className="match-group-menu-dropdown" style={{
          position: 'fixed',
          top: buttonRef.current.getBoundingClientRect().bottom + 4,
          right: window.innerWidth - buttonRef.current.getBoundingClientRect().right
        }}>
          <button
            className="match-group-menu-item"
            onClick={handleRenameClick}
          >
            Rename Group
          </button>
          <button
            className="match-group-menu-item destructive"
            onClick={handleDeleteClick}
          >
            Delete Group
          </button>
        </div>,
        document.body
      )}
    </div>
  );
};

export default MatchGroup;

